import React from 'react';
import { Search, Filter, Download } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';

type Props = {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  typeFilter: string;
  setTypeFilter: (value: string) => void;
  onExport: () => void;
};

export const VerificationFilters = ({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  typeFilter,
  setTypeFilter,
  onExport
}: Props) => {
  const { translations } = useLanguage();

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm space-y-4">
      {/* Main Filters Container */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Search - Takes 5 columns on large screens */}
        <div className="lg:col-span-5">
          <div className="relative">
            <input
              type="text"
              placeholder={translations?.verificationApproval?.filters?.searchPlaceholder || 'Search...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          </div>
        </div>

        {/* Status Filter - Takes 2 columns on large screens */}
        <div className="lg:col-span-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">
              {translations?.verificationApproval?.filters?.allStatuses || 'All Statuses'}
            </option>
            <option value="pending">
              {translations?.verificationApproval?.filters?.pending || 'Pending'}
            </option>
            <option value="approved">
              {translations?.verificationApproval?.filters?.approved || 'Approved'}
            </option>
            <option value="rejected">
              {translations?.verificationApproval?.filters?.rejected || 'Rejected'}
            </option>
          </select>
        </div>

        {/* Type Filter - Takes 2 columns on large screens */}
        <div className="lg:col-span-2">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">
              {translations?.verificationApproval?.filters?.allTypes || 'All Types'}
            </option>
            <option value="guide">
              {translations?.verificationApproval?.filters?.guides || 'Guides'}
            </option>
            <option value="organizer">
              {translations?.verificationApproval?.filters?.organizers || 'Organizers'}
            </option>
          </select>
        </div>

        {/* Action Buttons Container - Takes 3 columns on large screens */}
        <div className="lg:col-span-3 flex gap-2">
          {/* Export Button */}
          <button
            onClick={onExport}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
          >
            <Download size={20} />
            <span className="whitespace-nowrap">
              {translations?.verificationApproval?.buttons?.export || 'Export'}
            </span>
          </button>
        </div>
      </div>

      {/* Active Filters */}
      {(searchTerm || statusFilter !== 'all' || typeFilter !== 'all') && (
        <div className="flex flex-wrap items-center gap-2">
          <Filter size={16} className="text-primary-500" />
          <span className="text-sm text-gray-600">
            {translations?.verificationApproval?.filters?.activeFilters || 'Active Filters:'}
          </span>
          <div className="flex flex-wrap gap-2">
            {searchTerm && (
              <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                {translations?.verificationApproval?.filters?.search || 'Search'}: {searchTerm}
              </span>
            )}
            {statusFilter !== 'all' && (
              <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                {translations?.verificationApproval?.filters?.status || 'Status'}: {statusFilter}
              </span>
            )}
            {typeFilter !== 'all' && (
              <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                {translations?.verificationApproval?.filters?.type || 'Type'}: {typeFilter}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};